import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import {
  HeartPulse, Shield, Car, Home as HomeIcon, Scale, Gavel, Plane, Building2, ArrowUpRight, Check, FileText,
} from "lucide-react";

const iconFor = {
  krankenversicherung: HeartPulse,
  lebensversicherung: Shield,
  motorfahrzeug: Car,
  hausrat: HomeIcon,
  privathaftpflicht: Scale,
  rechtsschutz: Gavel,
  reise: Plane,
  kmu: Building2,
  steuererklaerung: FileText,
};

export default function Services() {
  const { t } = useLang();
  return (
    <div data-testid="page-services" className="bg-white text-[#0A0A0A]">
      {/* Hero */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden">
        <div className="absolute top-10 -right-32 w-[500px] h-[500px] bg-[#EA6A1A]/6 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#FFF1E6] border border-[#EA6A1A]/20 text-[#EA6A1A] px-3 py-1.5 rounded-full text-xs font-medium mb-6">
              <span className="w-1.5 h-1.5 bg-[#EA6A1A] rounded-full" />
              {t.servicesPage.eyebrow}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.03em] font-medium">
              {t.servicesPage.title.split(" ").slice(0, -2).join(" ")}{" "}
              <span className="italic text-[#EA6A1A] font-normal">{t.servicesPage.title.split(" ").slice(-2).join(" ")}</span>
            </h1>
            <p className="mt-8 text-lg text-[#52525B] max-w-2xl mx-auto leading-relaxed">
              {t.servicesPage.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service cards */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.servicesList.map((s, i) => {
              const Icon = iconFor[s.id] || Shield;
              const isFeatured = i === 0;
              return (
                <motion.article
                  key={s.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.05 }}
                  data-testid={`service-card-${s.id}`}
                  className={`group flex flex-col rounded-3xl p-8 border transition-all duration-300 card-shadow ${
                    isFeatured
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:-translate-y-1"
                      : "bg-white border-[#E9E9E7] hover:border-[#0A0A0A] hover:-translate-y-1"
                  }`}
                >
                  <div className="flex items-center justify-between mb-10">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isFeatured ? "bg-[#EA6A1A]" : "bg-[#F5F5F4] group-hover:bg-[#EA6A1A]"
                    } transition-colors`}>
                      <Icon className={isFeatured ? "text-white" : "text-[#0A0A0A] group-hover:text-white"} size={22} strokeWidth={1.6} />
                    </div>
                    <span className={`text-xs font-medium ${isFeatured ? "text-white/60" : "text-[#71717A]"}`}>
                      {String(i + 1).padStart(2, "0")} / {String(t.servicesList.length).padStart(2, "0")}
                    </span>
                  </div>

                  <h2 className="font-display text-2xl md:text-3xl font-medium mb-4 tracking-tight leading-tight">{s.t}</h2>
                  <p className={`leading-relaxed flex-1 text-[15px] ${isFeatured ? "text-white/70" : "text-[#71717A]"}`}>
                    {s.d}
                  </p>

                  <Link
                    to={s.id === "steuererklaerung" ? "/steuer" : "/contact"}
                    className={`mt-8 inline-flex items-center justify-between gap-2 rounded-full px-5 py-2.5 text-sm transition-all ${
                      isFeatured
                        ? "bg-white text-[#0A0A0A] hover:bg-[#EA6A1A] hover:text-white"
                        : "border border-[#E9E9E7] hover:border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    }`}
                    data-testid={`service-cta-${s.id}`}
                  >
                    <span>{s.id === "steuererklaerung" ? "Mehr erfahren" : t.nav.cta}</span>
                    <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Card */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative overflow-hidden bg-[#F7F5F0] rounded-3xl p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center border border-[#E9E9E7]"
          >
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 bg-white border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-5 text-[#52525B]">
                — Nächster Schritt
              </div>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-medium max-w-2xl">
                Eine <span className="italic text-[#EA6A1A]">persönliche</span> Beratung, ganz nach Ihrer Situation.
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#EA6A1A] text-white px-7 py-4 rounded-full transition-all shadow-sm"
              >
                <span className="text-sm font-medium">{t.nav.cta}</span>
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
